import { cn } from '../../utils/cn';

/**
 * RankingBadge — Hiển thị xếp hạng (A / B-Tốt / B-Đạt / C)
 * theo NĐ 278/2025 Phụ lục II v3.1.
 *
 * Variants:
 *  - 'pill'  : badge nhỏ dạng viên thuốc (dùng trong table, card)
 *  - 'card'  : card lớn có điểm số (dùng trong dashboard header)
 *  - 'icon'  : chỉ icon không text (dùng trong list compact)
 */

export type RankingValue = 'A' | 'B_TOT' | 'B_DAT' | 'B' | 'C' | null | undefined;

interface RankingBadgeProps {
  ranking: RankingValue;
  score?: number | null;
  variant?: 'pill' | 'card' | 'icon';
  showScore?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

// ─── Config bảng tra ──────────────────────────────────────────────────────────

const RANKING_CONFIG: Record<string, {
  label: string;
  sublabel: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
  dotClass: string;
  emoji: string;
  description: string;
}> = {
  A: {
    label: 'Loại A',
    sublabel: 'Xuất sắc',
    bgClass: 'bg-emerald-500/10 dark:bg-emerald-500/15',
    textClass: 'text-emerald-700 dark:text-emerald-400',
    borderClass: 'border-emerald-500/30',
    dotClass: 'bg-emerald-500',
    emoji: '★',
    description: '≥ 80 điểm & TC7b = 0',
  },
  B_TOT: {
    label: 'Loại B',
    sublabel: 'Tốt',
    bgClass: 'bg-sky-500/10 dark:bg-sky-500/15',
    textClass: 'text-sky-700 dark:text-sky-400',
    borderClass: 'border-sky-500/30',
    dotClass: 'bg-sky-500',
    emoji: '◆',
    description: '70–79 điểm',
  },
  B_DAT: {
    label: 'Loại B',
    sublabel: 'Đạt',
    bgClass: 'bg-blue-500/10 dark:bg-blue-500/15',
    textClass: 'text-blue-700 dark:text-blue-400',
    borderClass: 'border-blue-500/30',
    dotClass: 'bg-blue-500',
    emoji: '◇',
    description: '60–69 điểm',
  },
  // Phase I backward compat
  B: {
    label: 'Loại B',
    sublabel: '',
    bgClass: 'bg-sky-500/10 dark:bg-sky-500/15',
    textClass: 'text-sky-700 dark:text-sky-400',
    borderClass: 'border-sky-500/30',
    dotClass: 'bg-sky-500',
    emoji: '◆',
    description: '70–84 điểm',
  },
  C: {
    label: 'Loại C',
    sublabel: 'Không đạt',
    bgClass: 'bg-rose-500/10 dark:bg-rose-500/15',
    textClass: 'text-rose-700 dark:text-rose-400',
    borderClass: 'border-rose-500/30',
    dotClass: 'bg-rose-500',
    emoji: '✕',
    description: '< 60 điểm hoặc vi phạm HC',
  },
};

const UNKNOWN_CONFIG = {
  label: '—',
  sublabel: 'Chưa xếp hạng',
  bgClass: 'bg-slate-100 dark:bg-white/5',
  textClass: 'text-slate-500 dark:text-slate-400',
  borderClass: 'border-slate-200 dark:border-white/10',
  dotClass: 'bg-slate-400',
  emoji: '?',
  description: 'Chưa có kết quả',
};

const SIZE_CLASSES = {
  xs:  { text: 'text-[9px]',  px: 'px-2 py-0.5',  dot: 'w-1.5 h-1.5' },
  sm:  { text: 'text-[10px]', px: 'px-2.5 py-1',  dot: 'w-2 h-2'     },
  md:  { text: 'text-xs',     px: 'px-3 py-1.5',  dot: 'w-2 h-2'     },
  lg:  { text: 'text-sm',     px: 'px-4 py-2',    dot: 'w-2.5 h-2.5' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function RankingBadge({
  ranking,
  score,
  variant = 'pill',
  showScore = false,
  className,
  size = 'sm',
}: RankingBadgeProps) {
  const key = ranking ?? '__unknown__';
  const cfg = RANKING_CONFIG[key] ?? UNKNOWN_CONFIG;
  const sz  = SIZE_CLASSES[size];

  if (variant === 'pill') {
    return (
      <span
        title={cfg.description}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full font-light tracking-wide border',
          cfg.bgClass, cfg.textClass, cfg.borderClass,
          sz.px, sz.text,
          className,
        )}
      >
        <span className={cn('rounded-full flex-shrink-0', cfg.dotClass, sz.dot)} />
        {cfg.label}
        {cfg.sublabel && (
          <span className="opacity-70">· {cfg.sublabel}</span>
        )}
        {showScore && score != null && (
          <span className="ml-1 opacity-80 font-medium">{score.toFixed(1)}</span>
        )}
      </span>
    );
  }

  if (variant === 'card') {
    return (
      <div
        title={cfg.description}
        className={cn(
          'flex flex-col items-center justify-center gap-1 rounded-2xl border p-4 min-w-[80px]',
          cfg.bgClass, cfg.borderClass,
          className,
        )}
      >
        <span className={cn('text-2xl font-heading', cfg.textClass)}>
          {cfg.emoji}
        </span>
        <span className={cn('font-semibold text-sm', cfg.textClass)}>{cfg.label}</span>
        {cfg.sublabel && (
          <span className={cn('text-[10px] opacity-70', cfg.textClass)}>{cfg.sublabel}</span>
        )}
        {showScore && score != null && (
          <span className={cn('text-lg font-heading font-light mt-1', cfg.textClass)}>
            {score.toFixed(1)}đ
          </span>
        )}
      </div>
    );
  }

  // icon variant
  return (
    <span
      title={`${cfg.label}${cfg.sublabel ? ' · ' + cfg.sublabel : ''} — ${cfg.description}`}
      className={cn(
        'inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-semibold border',
        cfg.bgClass, cfg.textClass, cfg.borderClass,
        className,
      )}
    >
      {cfg.emoji}
    </span>
  );
}

/**
 * Helper: chuyển string ranking từ API → RankingValue
 */
export function parseRanking(r: string | null | undefined): RankingValue {
  if (!r) return null;
  const map: Record<string, RankingValue> = {
    A: 'A', B_TOT: 'B_TOT', B_DAT: 'B_DAT', B: 'B', C: 'C',
    'B-Tốt': 'B_TOT', 'B-Đạt': 'B_DAT',
  };
  return map[r] ?? null;
}
